/**
 * FilterChip — toggleable pill used by the collections search bar
 * for difficulty + completed filters.
 *
 * Active state uses the theme primary color; inactive uses the card
 * background with a subtle border. Includes an `accessibilityState`
 * with `selected` so screen readers announce the toggle state.
 */

import React from 'react';
import { StyleSheet, Text, TouchableOpacity } from 'react-native';

import { useDisplayTheme } from '../../hooks/useDisplayTheme';

export interface FilterChipProps {
  label: string;
  active: boolean;
  onPress: () => void;
  testID: string;
  accessibilityLabel: string;
}

export const FilterChip = React.memo(function FilterChip({
  label,
  active,
  onPress,
  testID,
  accessibilityLabel,
}: FilterChipProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  return (
    <TouchableOpacity
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected: active }}
      onPress={onPress}
      activeOpacity={0.75}
      style={[
        styles.chip,
        {
          backgroundColor: active ? theme.primary : theme.card,
          borderColor: active ? theme.primary : theme.border,
        },
      ]}
    >
      <Text
        allowFontScaling={false}
        style={{
          fontSize: 12 * fontScale,
          color: active ? '#fff' : theme.textSecondary,
          fontWeight: '600',
          ...ffs,
        }}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
  },
});
